import React, { useCallback, useEffect, useState } from "react";
import MediaUploader from "../../components/MediaUploader";
import api, { formatApiError, thumbUrl } from "../../lib/api";
import { useToast } from "../../lib/toast";

const EMPTY_STORY = { quote: "", name: "", role: "", image: "", artists_link: "", event_type: "" };

export default function AdminCustomerStories() {
  const toast = useToast();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_STORY);
  const [eventTypes, setEventTypes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/homepage/customer-stories");
      if (!Array.isArray(data)) throw new Error("Customer stories response is not a list");
      setStories(data);
    } catch (error) {
      toast(`Could not load customer stories: ${formatApiError(error)}`, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Event types with how many live artists chose each, for the story picker.
  useEffect(() => {
    api.get("/admin/homepage/event-types")
      .then(({ data }) => setEventTypes(Array.isArray(data?.items) ? data.items : []))
      .catch((error) => toast(`Could not load event types: ${formatApiError(error)}`, "error"));
  }, [toast]);

  const startNew = () => {
    setEditingId("");
    setDraft({ ...EMPTY_STORY });
  };

  const editStory = (story) => {
    setEditingId(story.id);
    setDraft({
      quote: story.quote || "",
      name: story.name || "",
      role: story.role || "",
      image: story.image || "",
      artists_link: story.artists_link || "",
      event_type: story.event_type || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_STORY });
  };

  const saveStory = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/admin/homepage/customer-stories/${encodeURIComponent(editingId)}`, draft);
        toast("Customer story updated.", "success");
      } else {
        await api.post("/admin/homepage/customer-stories", draft);
        toast("Customer story added.", "success");
      }
      await load();
      cancelEdit();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const updateStory = async (story, changes) => {
    setBusyId(story.id);
    try {
      await api.patch(`/admin/homepage/customer-stories/${encodeURIComponent(story.id)}`, changes);
      await load();
      if (editingId === story.id && changes.enabled === false) cancelEdit();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setBusyId("");
    }
  };

  const moveStory = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= stories.length) return;
    const reordered = [...stories];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setBusyId(stories[index].id);
    try {
      await Promise.all(reordered.map((story, order) => (
        api.patch(`/admin/homepage/customer-stories/${encodeURIComponent(story.id)}`, { order })
      )));
      await load();
    } catch (error) {
      toast(formatApiError(error), "error");
      await load();
    } finally {
      setBusyId("");
    }
  };

  const deleteStory = async (story) => {
    if (!window.confirm(`Remove "${story.name}" from the homepage Customer Stories slider?`)) return;
    setBusyId(story.id);
    try {
      await api.delete(`/admin/homepage/customer-stories/${encodeURIComponent(story.id)}`);
      toast("Customer story removed.", "success");
      if (editingId === story.id) cancelEdit();
      await load();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setBusyId("");
    }
  };

  const imageUploaded = (upload) => {
    if (!upload?.id) {
      toast("The image upload completed without a media ID.", "error");
      return;
    }
    setDraft((current) => ({ ...current, image: upload.id }));
    toast("Image uploaded. Save the story to publish the changes.", "success");
  };

  return (
    <div className="pad-24" data-testid="admin-customer-stories">
      <h2 className="font-serif fs-24 fw-700 mb-8">Homepage Customer Stories</h2>
      <p className="text-muted fs-13 mb-16">
        Manage the image slider directly below the homepage hero. Add as many stories as you need, then reorder or hide them.
      </p>

      <div className="card card-pad mb-16">
        <div className="flex-between mb-12" style={{ gap: "var(--space-3)", flexWrap: "wrap" }}>
          <h3 className="fs-16 fw-700">Stories ({stories.length})</h3>
          <button type="button" className="btn btn-gold btn-sm" onClick={startNew} disabled={editingId === ""}>
            Add story
          </button>
        </div>
        {loading ? (
          <p className="text-muted fs-13">Loading customer stories…</p>
        ) : stories.length === 0 ? (
          <p className="text-muted fs-13">No stories yet. Add one to show the slider on the homepage.</p>
        ) : (
          <div style={{ display: "grid", gap: "var(--space-2)" }}>
            {stories.map((story, index) => (
              <article
                key={story.id}
                className="card"
                data-testid={`admin-customer-story-${story.id}`}
                style={{ display: "grid", gridTemplateColumns: "88px minmax(0, 1fr)", gap: "var(--space-3)", alignItems: "center", padding: "var(--space-3)" }}
              >
                {story.image && (
                  <img
                    src={/^https?:\/\//i.test(story.image) ? story.image : thumbUrl(story.image)}
                    alt=""
                    loading="lazy"
                    style={{ width: 88, height: 64, objectFit: "cover", borderRadius: 8 }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <strong>{story.name}</strong>
                  {story.role && <span className="text-muted fs-12"> · {story.role}</span>}
                  <span className="text-muted fs-11"> · Opens {story.artists_link || (story.event_type ? `${story.event_type} artists` : "all artists")}</span>
                  <p className="text-muted fs-12" style={{ margin: "4px 0 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{story.quote}</p>
                  <div className="flex-row gap-8" style={{ flexWrap: "wrap" }}>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => editStory(story)}>Edit</button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs"
                      onClick={() => updateStory(story, { enabled: story.enabled === false })}
                      disabled={!!busyId}
                    >
                      {story.enabled === false ? "Show" : "Hide"}
                    </button>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => moveStory(index, -1)} disabled={index === 0 || !!busyId}>Move up</button>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => moveStory(index, 1)} disabled={index === stories.length - 1 || !!busyId}>Move down</button>
                    <button type="button" className="btn btn-red btn-xs" onClick={() => deleteStory(story)} disabled={!!busyId}>Remove</button>
                    {story.enabled === false && <span className="text-muted fs-11">Hidden</span>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {editingId !== null && (
        <form className="card card-pad" onSubmit={saveStory} data-testid="admin-customer-story-form">
          <div className="flex-between mb-12">
            <h3 className="fs-16 fw-700">{editingId ? "Edit story" : "Add customer story"}</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEdit}>Cancel</button>
          </div>
          <label className="field-label" htmlFor="customer-story-quote">Testimonial</label>
          <textarea
            id="customer-story-quote"
            className="field-input mb-12"
            rows={4}
            maxLength={1200}
            required
            value={draft.quote}
            onChange={(event) => setDraft((current) => ({ ...current, quote: event.target.value }))}
            data-testid="customer-story-quote"
          />
          <div className="grid-2 mb-12">
            <div>
              <label className="field-label" htmlFor="customer-story-name">Name</label>
              <input
                id="customer-story-name"
                className="field-input"
                required
                maxLength={120}
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                data-testid="customer-story-name"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="customer-story-role">Role / location</label>
              <input
                id="customer-story-role"
                className="field-input"
                maxLength={200}
                value={draft.role}
                onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value }))}
                placeholder="Event planner, Mumbai"
                data-testid="customer-story-role"
              />
            </div>
          </div>
          <label className="field-label" htmlFor="customer-story-event-type">Event type</label>
          <select
            id="customer-story-event-type"
            className="field-input"
            value={draft.event_type}
            onChange={(event) => setDraft((current) => ({ ...current, event_type: event.target.value }))}
            data-testid="customer-story-event-type"
          >
            <option value="">No event type (opens all artists)</option>
            {draft.event_type && !eventTypes.some((item) => item.name === draft.event_type) && (
              <option value={draft.event_type}>{draft.event_type}</option>
            )}
            {eventTypes.map((item) => (
              <option key={item.name} value={item.name}>
                {item.name} · {item.live_count} live {item.live_count === 1 ? "artist" : "artists"}
              </option>
            ))}
          </select>
          <p className="text-muted fs-11 mt-4 mb-12">
            Clicking the story opens Discover Artists showing only artists who chose this event type. Pick one with live artists;
            see Artist Event Types for who chose what.
          </p>
          <label className="field-label" htmlFor="customer-story-artists-link">Custom link (optional, overrides event type)</label>
          <input
            id="customer-story-artists-link"
            className="field-input"
            maxLength={500}
            value={draft.artists_link}
            onChange={(event) => setDraft((current) => ({ ...current, artists_link: event.target.value }))}
            placeholder="/search?category=DJ&city=Mumbai or /artist/artist-slug"
            data-testid="customer-story-artists-link"
          />
          <p className="text-muted fs-11 mt-4 mb-12">
            Only for special cases, e.g. one artist's profile (/artist/artist-slug). Leave empty to use the event type.
          </p>
          <label className="field-label" htmlFor="customer-story-image">Image URL or uploaded image ID</label>
          <input
            id="customer-story-image"
            className="field-input mb-12"
            required
            maxLength={1000}
            value={draft.image}
            onChange={(event) => setDraft((current) => ({ ...current, image: event.target.value }))}
            placeholder="Paste an image URL or upload below"
            data-testid="customer-story-image"
          />
          {draft.image && (
            <img
              src={/^https?:\/\//i.test(draft.image) ? draft.image : thumbUrl(draft.image)}
              alt="Customer story preview"
              style={{ width: "100%", maxWidth: 360, maxHeight: 180, objectFit: "cover", borderRadius: 8, marginBottom: 12 }}
            />
          )}
          <div className="mb-12">
            <MediaUploader type="gallery" accept="image/*" maxFiles={1} onComplete={imageUploaded} />
          </div>
          <button type="submit" className="btn btn-gold btn-sm" disabled={saving} data-testid="customer-story-save">
            {saving ? "Saving…" : "Save story"}
          </button>
        </form>
      )}
    </div>
  );
}
